import { useState, useEffect } from 'react';
import axios from 'axios';
import AppURL from '../components/AppUrl';

interface Employee {
    _id: string;
    name: string;
    email: string;
    role: string;
    password?: string;
    phone: string;
}

interface UserData {
    name: string;
    email: string;
    id: string;
    role: string;
}

const Employee = () => {
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [newEmployee, setNewEmployee] = useState<Omit<Employee, '_id'>>({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'employe'
    });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);


    const handleEmployeeClick = (employee: Employee) => {
        setSelectedEmployee(employee);
    };

    useEffect(() => {
        const fetchData = async () => {
            const userData = localStorage.getItem('userData');
            if (userData) {
                const parsedUser = JSON.parse(userData);
                setCurrentUser(parsedUser);

                try {
                    const response = await axios.get(`${AppURL}/api/employees/All`);
                    const allEmployees = response.data.employees.map((emp: any) => ({
                        _id: emp._id,
                        name: emp.name,
                        email: emp.email,
                        phone: emp.phone,
                        role: emp.role,
                        password: emp.password
                    }));

                    if (parsedUser.role !== 'manager') {
                        // Filtrer pour ne garder que l'employé correspondant à l'utilisateur connecté
                        const userEmployee = allEmployees.find((emp: { _id: any; }) => emp._id === parsedUser.id);
                        setEmployees(userEmployee ? [userEmployee] : []);
                    } else {
                        setEmployees(allEmployees);
                    }
                } catch (error) {
                    console.error('Erreur:', error);
                    setError('Erreur lors du chargement des données');
                }
            }
        };

        fetchData();
    }, []);


    const fetchEmployees = async () => {
        try {
            const response = await axios.get(`${AppURL}/api/employees/All`);
            const formattedEmployees = response.data.employees.map((emp: any) => ({
                _id: emp._id,
                name: emp.name,
                email: emp.email,
                phone: emp.phone,
                role: emp.role,
                password: emp.password
            }));
            setEmployees(formattedEmployees);
        } catch (error) {
            console.error('Erreur:', error);
            setError('Erreur lors du chargement des employés');
        }
    };

    const handleDeleteEmployee = async (id: string) => {
        const isConfirmed = window.confirm("Êtes-vous sûr de vouloir supprimer cet employé ?");
        if (!isConfirmed) return;

        try {
            await axios.delete(`${AppURL}/api/employees/deleteEmployee/${id}`);
            setShowModal(false);
            setSelectedEmployee(null);
            fetchEmployees();
        } catch (error) {
            console.error('Erreur de suppression:', error);
            setError('Erreur lors de la suppression de l\'employé');
        }
    };

    const handleUpdateEmployee = async () => {
        const isConfirmed = window.confirm("Êtes-vous sûr de vouloir mettre à jour cet employé ?");
        if (!isConfirmed) return;

        if (selectedEmployee) {
            try {
                const response = await axios.put(
                    `${AppURL}/api/employees/updateEmployee/${selectedEmployee._id}`,
                    selectedEmployee
                );
                if (response.data.success) {
                    setShowModal(false);
                    setSelectedEmployee(null);
                    // Recharger les données
                    const userData = localStorage.getItem('userData');
                    if (userData) {
                        const parsedUser = JSON.parse(userData);
                        const response = await axios.get(`${AppURL}/api/employees/All`);
                        const allEmployees = response.data.employees.map((emp: any) => ({
                            _id: emp._id,
                            name: emp.name,
                            email: emp.email,
                            phone: emp.phone,
                            role: emp.role,
                            password: emp.password
                        }));

                        if (parsedUser.role !== 'manager') {
                            const userEmployee = allEmployees.find((emp: { _id: any; }) => emp._id === parsedUser.id);
                            setEmployees(userEmployee ? [userEmployee] : []);
                        } else {
                            setEmployees(allEmployees);
                        }
                    }
                }
            } catch (error: any) {
                console.error('Erreur de mise à jour:', error);
                setError(error.response?.data?.message || 'Erreur lors de la mise à jour');
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewEmployee(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${AppURL}/api/employees/createEmployee`, newEmployee);
            if (response.data.success) {
                setShowModal(false);
                setNewEmployee({
                    name: '',
                    email: '',
                    phone: '',
                    password: '',
                    role: 'employe'
                });
                fetchEmployees();
            }
        } catch (error: any) {
            console.error('Erreur:', error);
            setError(error.response?.data?.message || 'Erreur lors de la création');
        }
    };

    return (
        <div className="p-4 sm:p-6">
            <h1 className="text-3xl sm:text-3xl font-bold mt-20 sm:mb-6">
                {currentUser?.role === 'manager' ? 'Employés' : 'Mes Informations'}
            </h1>

            {error && <div className="mb-4 text-red-500">{error}</div>}

            {/* Liste des employés */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">phone</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {employees.map((employee) => (
                            <tr
                                key={employee._id}
                                onClick={() => handleEmployeeClick(employee)}
                                className="cursor-pointer hover:bg-gray-50"
                            >
                                <td className="px-4 py-3 whitespace-nowrap">{employee.name}</td>
                                <td className="px-4 py-3 whitespace-nowrap">{employee.email}</td>
                                <td className="px-4 py-3 whitespace-nowrap">{employee.phone}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${employee.role === 'manager' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                        {employee.role === 'manager' ? 'Manager' : 'Employé'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bouton d'ajout - seulement pour les managers */}
            {currentUser?.role === 'manager' && (
                <button
                    onClick={() => setShowModal(true)}
                    className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-gray-800 text-white rounded-full p-4 shadow-lg hover:bg-blue-700 transition"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                </button>
            )}

            {/* Modal d'ajout - seulement pour les managers */}
            {showModal && currentUser?.role === 'manager' && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg sm:text-xl font-bold mb-4">Ajouter un employé</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Nom</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={newEmployee.name}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Email</label>
                                <input
                                    type="text"
                                    name="email"
                                    value={newEmployee.email.toLowerCase()}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                             <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Phone</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={newEmployee.phone}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Mot de passe</label>
                                <input
                                    type="text"
                                    name="password"
                                    value={newEmployee.password}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Rôle</label>
                                <select
                                    name="role"
                                    value={newEmployee.role}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded"
                                >
                                    <option value="employe">Employé</option>
                                    <option value="manager">Manager</option>
                                </select>
                            </div>
                            <div className="flex justify-end space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-blue-700"
                                >
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {selectedEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg sm:text-xl font-bold mb-4">
                            {currentUser?.role === 'manager' ? 'Modifier l\'employé' : 'Mes Informations'}
                        </h2>

                        <form onSubmit={(e) => e.preventDefault()}>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Nom</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={selectedEmployee.name}
                                    onChange={(e) => setSelectedEmployee({ ...selectedEmployee, name: e.target.value })}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Email</label>
                                <input
                                    type="text"
                                    name="email"
                                    value={selectedEmployee.email}
                                    onChange={(e) => setSelectedEmployee({ ...selectedEmployee, email: e.target.value })}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>

                             <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Phone</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={selectedEmployee.phone}
                                    onChange={(e) => setSelectedEmployee({ ...selectedEmployee, phone: e.target.value })}
                                    className="w-full p-2 border rounded"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Mot de passe</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        value={selectedEmployee.password || ''}
                                        onChange={(e) =>
                                            setSelectedEmployee({ ...selectedEmployee, password: e.target.value })
                                        }
                                        className="w-full p-2 border rounded pr-10"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 px-3 text-sm text-gray-600 hover:text-gray-900"
                                    >
                                        {showPassword ? 'Masquer' : 'Afficher'}
                                    </button>
                                </div>
                            </div>

                            {currentUser?.role === 'manager' && (
                                <div className="mb-4">
                                    <label className="block text-gray-700 mb-1">Rôle</label>
                                    <select
                                        name="role"
                                        value={selectedEmployee.role}
                                        onChange={(e) => setSelectedEmployee({ ...selectedEmployee, role: e.target.value })}
                                        className="w-full p-2 border rounded"
                                    >
                                        <option value="employe">Employé</option>
                                        <option value="manager">Manager</option>
                                    </select>
                                </div>
                            )}

                            <div className="flex justify-between space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setSelectedEmployee(null)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                    Annuler
                                </button>

                                {currentUser?.role === 'manager' && (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteEmployee(selectedEmployee._id)}
                                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                                    >
                                        Supprimer
                                    </button>
                                )}

                                <button
                                    type="button"
                                    onClick={handleUpdateEmployee}
                                    className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-blue-700"
                                >
                                    Mettre à jour
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Employee;